import React, { useState, useEffect } from "react";
import styles from "./WorkingHoursManagement.module.css";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiService } from "../../services/api";
import { useApp } from "../../contexts/AppContext";

export default function WorkingHoursManagement() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { refreshAppSettings } = useApp();
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [workingHour, setWorkingHour] = useState<{
    ru: string;
    en: string;
  }>({
    ru: "10:00 - 21:00",
    en: "10:00 - 21:00",
  });
  const [settingsInitialized, setSettingsInitialized] = useState(false);

  const { data: appSettings, isLoading } = useQuery({
    queryKey: ["appSettings"],
    queryFn: () => apiService.getAppSettings(),
  });

  const updateSettingsMutation = useMutation({
    mutationFn: apiService.updateAppSettings,
    onSuccess: async (data) => {
      if (data?.workingHour) {
        setWorkingHour(data.workingHour);
        setSettingsInitialized(true);
      }
      queryClient.invalidateQueries({ queryKey: ["appSettings"] });
      setSuccess("Настройки успешно обновлены!");
      setError("");
      await refreshAppSettings();
      setTimeout(() => setSuccess(""), 3000);
    },
    onError: (error: any) => {
      const validationErrors = error?.response?.data?.errors;
      if (validationErrors && Array.isArray(validationErrors) && validationErrors.length > 0) {
        const errorMessages = validationErrors.map((err: any) => {
          const fieldName = err.param || "Поле";
          const message = err.msg || "Ошибка валидации";
          return `• ${fieldName}: ${message}`;
        });
        setError(`Ошибки валидации:\n${errorMessages.join("\n")}`);
      } else {
        setError(
          error.response?.data?.message || "Ошибка при обновлении настроек"
        );
      }
    },
  });

  // Load settings when component mounts (only once)
  useEffect(() => {
    if (appSettings && !settingsInitialized) {
      setWorkingHour(
        appSettings.workingHour || {
          ru: "10:00 - 21:00",
          en: "10:00 - 21:00",
        }
      );
      setSettingsInitialized(true);
    }
  }, [appSettings, settingsInitialized]);

  const handleWorkingHourChange = (lang: "ru" | "en", value: string) => {
    setWorkingHour((prev) => ({
      ...prev,
      [lang]: value,
    }));
  };

  const handleSave = () => {
    const validationMessages: string[] = [];

    if (!workingHour.ru.trim()) {
      validationMessages.push("• Часы работы (RU): необходимо заполнить");
    }

    if (!workingHour.en.trim()) {
      validationMessages.push("• Часы работы (EN): необходимо заполнить");
    }

    if (validationMessages.length > 0) {
      setError(`Ошибки валидации:\n${validationMessages.join("\n")}`);
      return;
    }

    updateSettingsMutation.mutate({
      workingHour,
    });
  };

  // Check if user is authenticated
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("Необходимо войти в систему для управления настройками");
    }
  }, []);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingSpinner}>Загрузка настроек...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.logo}>Управление временем работы</div>
        <button
          className={styles.backButton}
          onClick={() => navigate("/admin")}
        >
          Назад
        </button>
      </header>

      <main className={styles.mainContent}>
        <h1 className={styles.pageTitle}>Время работы</h1>

        {error && (
          <div
            className={styles.errorMessage}
            style={{ whiteSpace: "pre-line" }}
          >
            {error}
          </div>
        )}
        {success && <div className={styles.successMessage}>{success}</div>}

        <div className={styles.settingsSection}>
          <h2 className={styles.settingsTitle}>Настройки времени работы</h2>
          <div className={styles.settingsGrid}>
            <div className={styles.settingsItem}>
              <label className={styles.settingsLabel}>
                Часы работы (RU)
              </label>
              <input
                className={styles.settingsInput}
                type="text"
                value={workingHour.ru}
                onChange={(e) => handleWorkingHourChange("ru", e.target.value)}
                placeholder="10:00 - 21:00"
              />
            </div>
            <div className={styles.settingsItem}>
              <label className={styles.settingsLabel}>
                Часы работы (EN)
              </label>
              <input
                className={styles.settingsInput}
                type="text"
                value={workingHour.en}
                onChange={(e) => handleWorkingHourChange("en", e.target.value)}
                placeholder="10:00 - 21:00"
              />
            </div>
          </div>
          {localStorage.getItem("token") ? (
            <button
              className={styles.saveSettingsButton}
              onClick={handleSave}
              disabled={updateSettingsMutation.isPending}
            >
              {updateSettingsMutation.isPending
                ? "Сохранение..."
                : "Сохранить настройки"}
            </button>
          ) : (
            <button
              className={styles.loginButton}
              onClick={() => navigate("/admin/login")}
            >
              Войти в систему для управления настройками
            </button>
          )}
        </div>
      </main>
    </div>
  );
}

